import React, { memo, useMemo, useState } from 'react'
import {
  MessageRow as MessageRowData,
  UserChip,
} from '../../../generated/api/chat-room'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { getTimestamp } from '../../../helpers/date'
import { theme } from '../../../theme/theme'
import { Linkify } from '../../common/Linkify'
import { TextTooltip } from '../../common/Tooltip'
import { ChatMessageImages } from '../ChatMessage/ChatMessageImages/ChatMessageImages'
import { ChipAvatar, chipFullName } from './ChipAvatar'
import { messageRowMedia } from './messageRowMedia'
import { ReceiptPreview } from './ReceiptPreview'

interface MessageRowProps {
  row: MessageRowData
  readers: UserChip[]
  isOwn: boolean
  isGroupChat: boolean
  isNewest: boolean
  startsCluster: boolean
  showTimestamp: boolean
  onReadByClick: (messageId: string) => void
}

const MessageRowComponent = ({
  row,
  readers,
  isOwn,
  isGroupChat,
  isNewest,
  startsCluster,
  showTimestamp,
  onReadByClick,
}: MessageRowProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const [referenceElement, setReferenceElement] = useState<Element | null>(null)

  const timestamp = useMemo(
    () => getTimestamp(new Date(row.createdAt)),
    [row.createdAt]
  )
  const media = useMemo(() => messageRowMedia(row), [row])

  const authorName = row.author
    ? chipFullName(row.author)
    : translate('common.user.deletedUserName')
  const displayAvatar = startsCluster && !isOwn
  const displayName = isGroupChat && !isOwn && startsCluster

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`room-message-${row.id}`}
    >
      {showTimestamp && (
        <p
          style={theme.textVariants.captionBold}
          className={`flex h-[46px] min-w-full items-center justify-center text-neutral-50
            ${isNewest ? 'mb-5' : 'my-5'}`}
        >
          {timestamp}
        </p>
      )}
      <div
        className={`flex flex-row
          ${startsCluster && !isNewest ? 'mt-6' : 'mt-[3px]'}
          ${isOwn ? 'self-end' : ''}`}
      >
        {displayAvatar && (
          <TextTooltip
            title={authorName}
            className="mb-1 mr-2 -ml-8 self-end"
            referenceElement={referenceElement}
            useFlexibleWidth
          >
            <div ref={setReferenceElement}>
              <ChipAvatar chip={row.author} size={24} />
            </div>
          </TextTooltip>
        )}
        <div className="flex flex-col">
          {displayName && (
            <p
              style={theme.textVariants.fineprint}
              className="ml-[10px] text-neutral-30"
            >
              {authorName}
            </p>
          )}
          {row.text && (
            <div
              className={`inline-block max-w-xs flex-col whitespace-pre-wrap break-words rounded-2xl py-3 px-[13px] md:max-w-xl
                ${isOwn ? 'self-end bg-chat-blue text-white' : 'bg-neutral-92'}`}
            >
              <Linkify textClassName={isOwn ? 'text-white' : 'text-brandGreen'}>
                <span>{row.text}</span>
              </Linkify>
            </div>
          )}
          <ChatMessageImages media={media} messageAuthorIsCurrentUser={isOwn} />
        </div>
      </div>
      <ReceiptPreview
        messageId={row.id}
        authorId={row.authorId}
        readers={readers}
        isOwn={isOwn}
        onClick={() => onReadByClick(row.id)}
      />
    </div>
  )
}

export const MessageRow = memo(MessageRowComponent)
